import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { ThemeRow, ThemeTokens } from "@time-tracker/shared";

const MIN_REQUIRED_GLOWS = 3;

const isStringArray = (arr: unknown): arr is string[] =>
  Array.isArray(arr) && arr.every((item) => typeof item === "string");

const parseGlowColors = (raw: string | null | undefined): string[] | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isStringArray(parsed) && parsed.length > 0) {
      return parsed;
    }
  } catch {
    // Ignore parsing errors for invalid raw JSON strings, falling back safely to null
  }
  return null;
};

/**
 * Ordered Chronos tabs that the global page-swipe carousel cycles through.
 * Kept in sync with the tab order in `ChronosControlBar` so a horizontal swipe
 * moves to the visually adjacent tab. Each entry carries the localized label so
 * the swipe indicators announce the destination tab.
 */
const CHRONOS_TABS: { path: string; label: MessageDescriptor }[] = [
  { path: "/chronos", label: msg`Dashboard` },
  { path: "/chronos/timeline", label: msg`Timeline` },
  { path: "/chronos/projects", label: msg`Projects` },
  { path: "/chronos/timesheet", label: msg`Timesheet` },
  { path: "/chronos/deep-work", label: msg`Deep Work` },
  { path: "/chronos/analytics", label: msg`Analytics` },
  { path: "/chronos/team", label: msg`Team` },
  { path: "/chronos/settings", label: msg`Settings` },
];

export const getListNameForPath = (path: string, t: (message: MessageDescriptor) => string) => {
  const tab = CHRONOS_TABS.find((entry) => entry.path === path);
  return tab ? t(tab.label) : t(msg`Chronos Sync`);
};

export const getListContextIdForPath = (_path: string): string | undefined => undefined;

export const getGlowColors = (
  _path: string,
  listTheme: ThemeRow | null,
  globalTheme: ThemeRow | null,
  globalToks: ThemeTokens | null,
) => {
  const activeTheme = listTheme ?? globalTheme;
  if (activeTheme) {
    const glowArray = parseGlowColors(activeTheme.glow_colors);
    if (glowArray && glowArray.length >= MIN_REQUIRED_GLOWS) {
      return {
        glow0: glowArray[0],
        glow1: glowArray[1],
        glow2: glowArray[2],
      };
    }
  }

  if (globalToks && globalToks.id !== "none") {
    return {
      glow0: globalToks.glow0,
      glow1: globalToks.glow1,
      glow2: globalToks.glow2,
    };
  }

  return { glow0: "var(--glow-0)", glow1: "var(--glow-1)", glow2: "var(--glow-2)" };
};

export const getOrderedListPaths = () => CHRONOS_TABS.map((entry) => entry.path);
